import type { Task } from "~skillBuilder/utils/types";

import { image, link, tip, title } from "../../contentBlocks";

export const PConduct = {
  name: "Professional Conduct",
  uniqueName: "p-conduct",
  icon: "Lesson",
  durationTimeText: "10 min",
  steps: [
    // Step 1
    {
      nextButtonText: "Start",
      questions: [
        // Question 1.1
        {
          attribute: "professionalism.conduct/overview",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "Professional Conduct", durationTimeText: "10 min" })}
<p>Whether you're subbing for a single day or a frequent guest in the classroom, professionalism and positive connections make a lasting impact. Greet staff with a friendly attitude, engage with students respectfully, and demonstrate a team-player mindset. Building strong relationships fosters trust, enhances the learning environment, and opens doors for future opportunities!</p>
<div align="center">
<div style="max-width:250px;width:100%;">
${image({ url: "https://res.cloudinary.com/swingeducation/image/upload/fl_preserve_transparency/v1758202465/skillbuilderAssets/optimized/Teacher_thumbs_p.jpg", alt: "Man giving a thumbs up." })}
</div>
</div>
            `,
          },
        },
      ],
    },
    // Step 2
    {
      nextButtonText: "Next",
      questions: [
        // Question 2.1
        {
          attribute: "professionalism.conduct/instructions",
          label: "Following instructions",
          // description: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Adhering to school procedures, including bell schedules, emergency protocols, and the lesson plan, is key to maintaining a well-structured and supportive environment. By consistently following these guidelines, you'll build trust with staff and administration, reinforcing your reputation as a professional and reliable substitute teacher.
            `,
          },
        },
      ],
    },
    // Step 3
    {
      nextButtonText: "Next",
      questions: [
        // Question 3.1
        {
          attribute: "professionalism.conduct/environment",
          label: "Learning environment",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Students look to you for guidance—set clear expectations from the start and reinforce them with consistency. Positive reinforcement is essential for fostering a productive and respectful classroom.

If challenges arise, manage them calmly and professionally. Avoid raising your voice or touching students or their belongings.

${tip({ text: "Keep cell phones and headphones away and out of sight to respect the learning environment. Also, refrain from sitting or placing your feet on furniture—shared spaces should be respected by all!" })}
            `,
          },
        },
      ],
    },
    // Step 4
    {
      nextButtonText: "Next",
      questions: [
        // Question 4.1
        {
          attribute: "professionalism.conduct/conversations",
          label: "Professional conversations",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Maintain conversations that are appropriate and focused on student learning. Be cautious when engaging with students, and always ensure discussions remain on topic with the lesson plan and remain within professional boundaries.

${link({ text: "Can I, as a Swing Sub, share my social media with students?", url: "https://support.swingeducation.com/hc/en-us/articles/360049592754-Can-I-as-a-Swing-Sub-share-my-social-media-with-students" })}
            `,
          },
        },
      ],
    },
    // Step 5
    {
      nextButtonText: "Next Lesson",
      questions: [
        // Question 5.1
        {
          attribute: "professionalism.conduct/conflicts",
          label: "Handling conflicts",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
When conflicts arise, de-escalating quickly and calmly is crucial. Use these strategies to manage difficult situations effectively:

**Do:**
<ul style="margin-left: 16px;">
<li>✅ Stay calm—keep your composure to set the tone for resolution.</li>
<li>✅ Actively listen to the student(s) and acknowledge their feelings.</li>
<li>✅ Offer choices to guide students toward a solution.</li>
<li>✅ Redirect students back to the task at hand to refocus their learning.</li>
</ul>

**Don't:**
<ul style="margin-left: 16px;">
<li>❌ Don't raise your voice or demean the student(s).</li>
<li>❌ Don't engage in a power struggle.</li>
<li>❌ Don't demand control.</li>
<li>❌ Don't dwell on the issue—allow everyone, including yourself, a fresh start.</li>
</ul>
            `,
          },
        },
      ],
    },
  ],
} as const satisfies Task;
